import React, { useState, useRef, useEffect } from "react";
import Barcode from "react-barcode";
import type { BasketItem } from "../page";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";

interface PrintReceiptModalProps {
  onClose: () => void;
  paymentMethods?: Array<{ method: string; amount: number }>;
  basketItems: BasketItem[];
  subtotal: number;
  discount: number;
  orderDiscount?: number;
  tax: number;
  total: number;
  receiptData?: {
    receiptNo?: string;
    transactionId?: string;
    saleDate?: string;
    cashier?: string;
    terminal?: string;
    storeName?: string;
    storeAddress?: string;
    customerName?: string;
  };
}

export default function PrintReceiptModal({
  onClose,
  paymentMethods = [],
  basketItems,
  subtotal,
  orderDiscount = 0,
  total,
  receiptData,
}: PrintReceiptModalProps) {
  const [printType, setPrintType] = useState<"customer" | "merchant" | "both">(
    "both",
  );
  const [copies, setCopies] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const receiptPreviewRef = useRef<HTMLDivElement>(null);

  const printerConnected =
    isSunmiDevice() && (window.SunmiPrinter?.isConnected() ?? false);

  const toRateNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseDateSafe = (value: unknown): Date | null => {
    if (!value) return null;
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getActiveItemDiscount = (item: BasketItem) => {
    const now = new Date();
    const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
    return (
      discounts.find((discount) => {
        const from = parseDateSafe(
          discount?.effectiveFrom ??
            discount?.effectiveFromDate ??
            discount?.startDate ??
            discount?.fromDate ??
            discount?.validFrom,
        );
        const to = parseDateSafe(
          discount?.effectiveTo ??
            discount?.effectiveToDate ??
            discount?.endDate ??
            discount?.toDate ??
            discount?.validTo,
        );
        if (from && now < from) return false;
        if (to && now > to) return false;
        return true;
      }) ?? null
    );
  };

  const getItemDiscountAmount = (item: BasketItem): number => {
    const activeDiscount = getActiveItemDiscount(item);
    if (!activeDiscount) return 0;
    const lineBase = Number(item.price || 0) * Number(item.qty || 0);
    if (lineBase <= 0) return 0;
    const typeName = String(
      activeDiscount?.discountType?.name ??
        activeDiscount?.discountTypeName ??
        activeDiscount?.amountType?.name ??
        activeDiscount?.amountTypeName ??
        activeDiscount?.type ??
        activeDiscount?.valueType ??
        "",
    ).toLowerCase();
    const typeId = Number(
      activeDiscount?.discountType?.id ??
        activeDiscount?.discountTypeId ??
        activeDiscount?.amountType?.id ??
        activeDiscount?.amountTypeId ??
        -1,
    );
    const isPercentage =
      activeDiscount?.isPercentage === true ||
      typeId === 1 ||
      typeName.includes("percent");
    const discountValue = toRateNumber(
      activeDiscount?.percentage ??
        activeDiscount?.percent ??
        activeDiscount?.rate ??
        activeDiscount?.discountPercentage ??
        activeDiscount?.amount ??
        activeDiscount?.discountAmount ??
        activeDiscount?.value,
    );
    if (discountValue <= 0) return 0;
    let discountAmount = isPercentage
      ? lineBase * (discountValue / 100)
      : discountValue * Number(item.qty || 0);
    const maxValue = toRateNumber(
      activeDiscount?.maximumValue ??
        activeDiscount?.maximumDiscount ??
        activeDiscount?.maxDiscount ??
        activeDiscount?.maxValue ??
        activeDiscount?.maxAmount ??
        activeDiscount?.maximumAmount,
    );
    if (maxValue > 0) discountAmount = Math.min(discountAmount, maxValue);
    return Math.max(0, Math.min(discountAmount, lineBase));
  };

  const getItemVatRate = (item: BasketItem): number => {
    const taxes = Array.isArray(item?.taxes) ? item.taxes : [];
    const rate = taxes.reduce((sum, tax) => {
      const r = toRateNumber(
        tax?.rate ??
          tax?.taxRate ??
          tax?.percentage ??
          tax?.percent ??
          tax?.amount ??
          tax?.vatPercentage ??
          tax?.value ??
          tax?.tax?.rate ??
          tax?.tax?.amount,
      );
      return sum + Math.max(0, r);
    }, 0);
    return rate > 0 ? rate : 0;
  };

  const lineBreakdown = basketItems.map((item) => {
    const lineBase = Number(item.price || 0) * Number(item.qty || 0);
    const lineDiscount = getItemDiscountAmount(item);
    const vatRate = getItemVatRate(item);
    const taxableBase = Math.max(0, lineBase - lineDiscount);
    const lineVat = taxableBase * (vatRate / 100);
    return { ...item, lineDiscount, lineVat, vatRate };
  });

  const totalLineDiscount = lineBreakdown.reduce(
    (sum, item) => sum + item.lineDiscount,
    0,
  );
  const totalLineVat = lineBreakdown.reduce(
    (sum, item) => sum + item.lineVat,
    0,
  );
  const totalDiscount = totalLineDiscount + orderDiscount;

  const counterInfo = JSON.parse(
    localStorage.getItem("counterCheckedInInfo") || "{}",
  );
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const effectiveSaleDate = receiptData?.saleDate
    ? new Date(receiptData.saleDate)
    : new Date();

  const storeName = "Golfer";
  const orderNo =
    receiptData?.receiptNo ?? receiptData?.transactionId ?? `POS-${Date.now()}`;
  const dateShort = effectiveSaleDate.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
  const timeStr = effectiveSaleDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const cashier =
    receiptData?.cashier || userInfo?.name || userInfo?.email || "Cashier";
  const terminal =
    receiptData?.terminal || counterInfo?.counterName || "Terminal";
  const customerName = receiptData?.customerName || "Walk In Customer";

  const totalPaid = paymentMethods.reduce((sum, pm) => sum + pm.amount, 0);
  const change = totalPaid > total ? totalPaid - total : 0;

  // Pure black inline styles — thermal printers only support black; any gray renders blurred
  const BLK = "color:#000";
  const buildReceiptHtml = (copyType: "customer" | "merchant") => {
    const isCustomer = copyType === "customer";
    const itemsHtml = lineBreakdown
      .map(
        (item) => `
      <tr style="font-size:14px;font-weight:bold;${BLK}">
        <td style="padding:3px 2px 0 0;word-break:break-word;${BLK}">${item.name}</td>
        <td style="text-align:right;white-space:nowrap;padding:3px 0 0 2px;${BLK}" rowspan="2">${item.total.toFixed(2)}</td>
      </tr>
      <tr style="font-size:12px;${BLK}">
        <td style="padding:0 2px 3px 0;${BLK}">${item.qty} × ${item.price.toFixed(2)}${item.vatRate > 0 ? ` | Vat:${item.vatRate}%` : ""}${item.lineDiscount > 0 ? ` | Disc:${item.lineDiscount.toFixed(2)}` : ""}</td>
      </tr>`,
      )
      .join("");

    const paymentsHtml = paymentMethods
      .map(
        (pm) =>
          `<tr><td style="font-weight:bold;padding:3px 0;${BLK}">${pm.method}</td><td style="font-weight:bold;text-align:right;padding:3px 0;${BLK}">${pm.amount.toFixed(2)}</td></tr>`,
      )
      .join("");

    const footerHtml = isCustomer
      ? `<div style="text-align:center;margin-top:14px;border-top:1px dotted #000;padding-top:10px;${BLK}">
           <div style="margin-bottom:5px;font-size:15px;${BLK}">Thank you for shopping</div>
           <div style="font-weight:bold;font-size:15px;${BLK}">Customer Copy</div>
         </div>`
      : `<div style="text-align:center;margin-top:14px;border-top:1px dotted #000;padding-top:10px;${BLK}">
           <div style="font-weight:bold;font-size:15px;${BLK}">Merchant Copy</div>
         </div>`;

    return `
      <div style="font-family:'Courier New',Courier,monospace;font-size:15px;font-weight:bold;line-height:1.6;max-width:300px;${BLK}">
        <div style="text-align:center;margin-bottom:6px">
          <img src="${window.location.origin}/golfer_logo.jpg" alt="Golfer" style="width:64px;height:64px;object-fit:contain;display:block;margin:0 auto 2px" />
          <div style="font-size:20px;font-weight:bold;${BLK}">${storeName}</div>
        </div>
        <div style="border-bottom:1px dotted #000;margin:8px 0;padding:4px 0"></div>
        <div style="text-align:center;font-weight:bold;font-size:17px;margin:5px 0;${BLK}">Order Receipt</div>
        <div style="border-bottom:1px dotted #000;margin:8px 0;padding:4px 0"></div>
        <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
          <tr><td style="font-weight:bold;padding:2px 0;width:40%;${BLK}">Customer:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${customerName}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Order No:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${orderNo}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Date:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${dateShort}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Time:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${timeStr}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">User:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${cashier}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Terminal:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${terminal}</td></tr>
        </table>
        <div style="text-align:center;font-weight:bold;font-size:16px;margin:10px 0 15px;${BLK}">Sales Item</div>
        <table style="width:100%;border-collapse:collapse;font-size:14px;${BLK}">
          <thead>
            <tr style="border-top:1px dotted #000;border-bottom:1px dotted #000">
              <th style="text-align:left;padding:4px 2px 4px 0;font-weight:bold;${BLK}">Product</th>
              <th style="text-align:right;white-space:nowrap;padding:4px 0 4px 2px;font-weight:bold;${BLK}">Total</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <div style="border-bottom:1px dotted #000;margin:8px 0;padding:4px 0"></div>
        <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Subtotal</td><td style="font-weight:bold;text-align:right;padding:2px 0;${BLK}">${subtotal.toFixed(2)}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Vat(+)</td><td style="font-weight:bold;text-align:right;padding:2px 0;${BLK}">${totalLineVat.toFixed(2)}</td></tr>
          ${totalDiscount > 0 ? `<tr><td style="font-weight:bold;padding:2px 0;${BLK}">You have saved</td><td style="font-weight:bold;text-align:right;padding:2px 0;${BLK}">${totalDiscount.toFixed(2)}</td></tr>` : ""}
        </table>
        <div style="border-top:1px dotted #000;border-bottom:2px solid #000;margin:5px;padding:4px 0 20px">
          <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
            <tr><td style="font-weight:bold;${BLK}">Total</td><td style="text-align:right;font-weight:bold;${BLK}">${total.toFixed(2)}</td></tr>
          </table>
        </div>
        <div style="text-align:center;font-weight:bold;font-size:15px;margin:10px 0 3px;${BLK}">Payments</div>
        <div style="border-bottom:1px dotted #000;margin:5px 0;padding:4px 0"></div>
        <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
          ${paymentsHtml}
          <tr><td style="font-weight:bold;padding:3px 0;${BLK}">Total Payment</td><td style="font-weight:bold;text-align:right;padding:3px 0;${BLK}">${totalPaid.toFixed(2)}</td></tr>
          ${change > 0 ? `<tr><td style="font-weight:bold;padding:3px 0;${BLK}">Change</td><td style="font-weight:bold;text-align:right;padding:3px 0;${BLK}">${change.toFixed(2)}</td></tr>` : ""}
        </table>
        <div style="text-align:center;margin:18px 0 5px">
          <canvas id="barcode-${copyType}"></canvas>
          <div style="font-size:14px;margin-top:3px;${BLK}">${orderNo}</div>
        </div>
        ${footerHtml}
        <div style="text-align:right;font-size:12px;${BLK};margin-top:10px">Powered by biznify.net</div>
      </div>`;
  };

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptPreviewRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error(
          "Printer not ready. Please check the printer and try again.",
        );
        return;
      }
      setIsPrinting(true);
      try {
        if (printType === "both") {
          const customerEl = receiptPreviewRef.current!.querySelector(
            '[data-copy="customer"]',
          ) as HTMLElement | null;
          const merchantEl = receiptPreviewRef.current!.querySelector(
            '[data-copy="merchant"]',
          ) as HTMLElement | null;
          for (let i = 0; i < copies; i++)
            await printOnSunmi(customerEl ?? receiptPreviewRef.current!, 1);
          for (let i = 0; i < copies; i++)
            await printOnSunmi(merchantEl ?? receiptPreviewRef.current!, 1);
        } else {
          await printOnSunmi(receiptPreviewRef.current, copies);
        }
        onClose();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      setIsPrinting(false);
      return;
    }

    setIsPrinting(true);

    const pages: Array<"customer" | "merchant"> = [];
    if (printType === "both") {
      for (let i = 0; i < copies; i++) pages.push("customer");
      for (let i = 0; i < copies; i++) pages.push("merchant");
    } else {
      for (let i = 0; i < copies; i++) pages.push(printType);
    }

    const pagesHtml = pages
      .map(
        (type, i) =>
          `<div class="page">${buildReceiptHtml(type)}</div>${i < pages.length - 1 ? '<div class="page-break"></div>' : ""}`,
      )
      .join("");

    const html = `<!DOCTYPE html><html><head>
      <title>Receipt - ${orderNo}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        html,body{background:#fff;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:'Courier New',Courier,monospace;font-size:15px;font-weight:bold}
        .page{max-width:300px;margin:0 auto;padding:12px}
        .page-break{page-break-after:always;break-after:page}
        @media print{
          *{color:#000 !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          body{margin:0;padding:0}
          .page{padding:4px;max-width:100%}
          .page-break{page-break-after:always;break-after:page}
        }
      </style>
    </head>
    <body>
      ${pagesHtml}
      <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
      <script>
        window.onload = function() {
          if(typeof JsBarcode !== 'undefined') {
            document.querySelectorAll('canvas[id^="barcode-"]').forEach(function(el) {
              try { JsBarcode(el, '${orderNo}', {format:'CODE128',width:2,height:60,displayValue:false,margin:0,lineColor:'#000',background:'#fff'}); } catch(e){}
            });
          }
          setTimeout(function(){ window.print(); window.close(); }, 500);
        };
      <\/script>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    setTimeout(() => {
      URL.revokeObjectURL(url);
      setIsPrinting(false);
      onClose();
    }, 2000);
    if (!win) setIsPrinting(false);
  };

  const handlePrintRef = useRef(handlePrint);
  useEffect(() => {
    handlePrintRef.current = handlePrint;
  });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !isPrinting) handlePrintRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isPrinting]);

  // Pure black styles for preview — mirrors actual print output
  const blk: React.CSSProperties = { color: "#000" };
  const renderReceiptPreview = (copyType: "customer" | "merchant") => {
    const isCustomer = copyType === "customer";
    // Base 14px; section headings 16px; store name 18px
    return (
      <div
        style={{
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: "16px",
          fontWeight: "bold",
          lineHeight: "1.4",
          color: "#000",
        }}
      >
        {/* Store */}
        <div style={{ textAlign: "center", marginBottom: "3px" }}>
          <img
            src="/golfer_logo.jpg"
            alt="Golfer"
            style={{
              width: "60px",
              height: "60px",
              objectFit: "contain",
              display: "block",
              margin: "0 auto 2px",
            }}
          />
          <div style={{ fontSize: "20px", fontWeight: "bold", ...blk }}>
            {storeName}
          </div>
        </div>
        <div
          style={{
            borderBottom: "1px dotted #000",
            margin: "3px 0",
            padding: "4px 0",
          }}
        ></div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "18px",
            ...blk,
          }}
        >
          Order Receipt
        </div>
        <div
          style={{
            borderBottom: "1px dotted #000",
            margin: "3px 0",
            padding: "4px 0",
          }}
        ></div>

        {/* Info */}
        <table style={{ width: "100%", fontSize: "16px", ...blk }}>
          <tbody>
            {[
              ["Customer:", customerName],
              ["Order No:", orderNo],
              ["Date:", dateShort],
              ["Time:", timeStr],
              ["User:", cashier],
              ["Terminal:", terminal],
            ].map(([label, value]) => (
              <tr key={label}>
                <td
                  style={{
                    fontWeight: "bold",
                    width: "40%",
                    padding: "0",
                    ...blk,
                  }}
                >
                  {label}
                </td>
                <td style={{ padding: "0", ...blk }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Sales Item */}
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "18px",
            margin: "3px 0 15px",
            ...blk,
          }}
        >
          Sales Item
        </div>
        <table
          style={{
            width: "100%",
            fontSize: "12px",
            borderCollapse: "collapse",
            tableLayout: "auto",
            ...blk,
          }}
        >
          <thead>
            <tr
              style={{
                borderTop: "1px dotted #000",
                borderBottom: "1px dotted #000",
              }}
            >
              <th
                style={{
                  textAlign: "left",
                  padding: "4px 2px 4px 0",
                  fontWeight: "bold",
                  ...blk,
                }}
              >
                Product
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "4px 0 4px 2px",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  ...blk,
                }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {lineBreakdown.map((item, idx) => (
              <React.Fragment key={idx}>
                <tr>
                  <td
                    style={{
                      padding: "3px 2px 0 0",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      fontWeight: "bold",
                      ...blk,
                    }}
                  >
                    {item.name}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      padding: "3px 0 0 2px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      ...blk,
                    }}
                    rowSpan={2}
                  >
                    {item.total.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{ padding: "0 2px 3px 0", fontSize: "11px", ...blk }}
                  >
                    {item.qty} × {item.price.toFixed(2)}
                    {item.vatRate > 0 ? ` | Vat:${item.vatRate}%` : ""}
                    {item.lineDiscount > 0
                      ? ` | Disc:${item.lineDiscount.toFixed(2)}`
                      : ""}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <div
          style={{
            borderBottom: "1px dotted #000",
            margin: "3px 0",
            padding: "4px 0",
          }}
        ></div>

        {/* Totals */}
        <table style={{ width: "100%", fontSize: "16px", ...blk }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                Subtotal
              </td>
              <td style={{ textAlign: "right", padding: "0", ...blk }}>
                {subtotal.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                Vat(+)
              </td>
              <td style={{ textAlign: "right", padding: "0", ...blk }}>
                {totalLineVat.toFixed(2)}
              </td>
            </tr>
            {totalDiscount > 0 && (
              <tr>
                <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                  You have saved
                </td>
                <td style={{ textAlign: "right", padding: "0", ...blk }}>
                  {totalDiscount.toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div
          style={{
            borderTop: "1px dotted #000",
            borderBottom: "2px solid #000",
            margin: "3px 0",
            padding: "1px 0",
          }}
        >
          <table
            style={{ width: "100%", fontSize: "18px", padding: "5px", ...blk }}
          >
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                  Total
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: "bold",
                    padding: "4px 0 20px",
                    ...blk,
                  }}
                >
                  {total.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Payments */}
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "18px",
            margin: "3px 0",
            ...blk,
          }}
        >
          Payments
        </div>
        <div
          style={{
            borderBottom: "1px dotted #000",
            margin: "3px 0",
            padding: "4px 0",
          }}
        ></div>
        <table style={{ width: "100%", fontSize: "16px", ...blk }}>
          <tbody>
            {paymentMethods.map((pm, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                  {pm.method}
                </td>
                <td style={{ textAlign: "right", padding: "0", ...blk }}>
                  {pm.amount.toFixed(2)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                Total Payment
              </td>
              <td style={{ textAlign: "right", padding: "0", ...blk }}>
                {totalPaid.toFixed(2)}
              </td>
            </tr>
            {change > 0 && (
              <tr>
                <td style={{ fontWeight: "bold", padding: "0", ...blk }}>
                  Change
                </td>
                <td style={{ textAlign: "right", padding: "0", ...blk }}>
                  {change.toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Barcode */}
        <div
          style={{
            textAlign: "center",
            margin: "6px 0 2px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Barcode
            value={orderNo || "0000000000"}
            format="CODE128"
            width={1.2}
            height={50}
            displayValue={false}
            margin={0}
            lineColor="#000"
            background="#fff"
          />
          <div style={{ fontSize: "16px", marginTop: "1px", ...blk }}>
            {orderNo}
          </div>
        </div>

        {/* Footer */}
        {isCustomer ? (
          <div
            style={{
              textAlign: "center",
              borderTop: "1px dotted #000",
              paddingTop: "4px",
              marginTop: "4px",
              fontSize: "16px",
              ...blk,
            }}
          >
            <div style={blk}>Thank you for shopping</div>
            <div style={{ fontWeight: "bold", ...blk }}>Customer Copy</div>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              borderTop: "1px dotted #000",
              paddingTop: "4px",
              marginTop: "4px",
              fontSize: "16px",
              ...blk,
            }}
          >
            <div style={{ fontWeight: "bold", ...blk }}>Merchant Copy</div>
          </div>
        )}
        <div
          style={{
            textAlign: "right",
            fontSize: "14px",
            marginTop: "4px",
            ...blk,
          }}
        >
          Powered by biznify.net
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Print Receipt</h2>
            <p className="text-sm text-gray-300">Configure and preview</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Left: receipt type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Receipt Type
              </label>
              <div className="space-y-2">
                {(["customer", "merchant", "both"] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setPrintType(type)}
                    className={`w-full p-3 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-3 ${printType === type ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                  >
                    <div
                      className={`w-5 h-5 flex items-center justify-center rounded-full border-2 transition-colors ${printType === type ? "bg-teal-600 border-teal-600" : "border-gray-300"}`}
                    >
                      {printType === type && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">
                        {type === "customer"
                          ? "Customer Copy"
                          : type === "merchant"
                            ? "Merchant Copy"
                            : "Both Copies"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {type === "customer"
                          ? "Standard receipt for customer"
                          : type === "merchant"
                            ? "Store record copy"
                            : "Print customer and merchant"}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right: copies + printer status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Number of Copies
              </label>
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setCopies(Math.max(1, copies - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg cursor-pointer"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <input
                  type="number"
                  onWheel={(e) => e.currentTarget.blur()}
                  value={copies}
                  onChange={(e) =>
                    setCopies(
                      Math.max(
                        1,
                        Math.min(5, parseInt(e.target.value, 10) || 1),
                      ),
                    )
                  }
                  className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-center text-lg font-bold focus:outline-none focus:border-teal-500"
                  min="1"
                  max="5"
                />
                <button
                  onClick={() => setCopies(Math.min(5, copies + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <div
                className={`mt-4 p-3 rounded-lg border ${printerConnected ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
              >
                <div className="flex items-center gap-2">
                  <i
                    className={`ri-printer-line text-lg ${printerConnected ? "text-blue-600" : "text-red-500"}`}
                  ></i>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${printerConnected ? "bg-green-500" : "bg-red-500"}`}
                    ></div>
                    <p
                      className={`text-xs ${printerConnected ? "text-blue-800" : "text-red-700"}`}
                    >
                      {printerConnected
                        ? "Printer Connected"
                        : "Printer Not Connected"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900 mb-3">
              Receipt Preview
            </h3>
            {printType === "both" ? (
              <div>
                <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <i className="ri-information-line text-blue-500"></i>
                  <p className="text-xs text-blue-700 font-medium">
                    2 receipts will be printed separately — one for customer,
                    one for merchant
                  </p>
                </div>
                <div ref={receiptPreviewRef} className="flex flex-col gap-6">
                  {(["customer", "merchant"] as const).map((copyType) => (
                    <div
                      key={copyType}
                      data-copy={copyType}
                      className="mx-auto"
                      style={{ width: "302px" }}
                    >
                      <div className="bg-white">
                        {renderReceiptPreview(copyType)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div
                ref={receiptPreviewRef}
                className="mx-auto"
                style={{ width: "302px" }}
              >
                <div className="bg-white">
                  {renderReceiptPreview(printType)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className={`px-8 py-3 rounded-lg font-bold transition-colors ${isPrinting ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700 text-white cursor-pointer"}`}
          >
            {isPrinting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Printing...
              </>
            ) : (
              <>
                <i className="ri-printer-line mr-2"></i>Print{" "}
                {copies > 1 ? `${copies} Copies` : "Receipt"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
